import type { AuditLogExportFormat } from '@qms360/shared';
import { apiFetch, apiFetchBlob } from './api.js';

/**
 * The Audit & Compliance Log (spec §13.7).
 *
 * Every call here is itself recorded by the server before it answers — reading
 * the log is evidence too (ADR-0016).
 */

export interface AuditLogEntry {
  id: string;
  occurredAt: string;
  /** Null for platform-level entries: sign-ins, sessions, access changes. */
  app: 'qaqc' | 'lesson-learned' | 'audit' | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  entityLabel: string | null;
  actorUserId: string | null;
  actorName: string | null;
  actorAuthSource: string | null;
  onBehalfOfName: string | null;
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
  sourceIp: string | null;
  requestId: string | null;
}

export interface AuditLogPage {
  entries: AuditLogEntry[];
  nextCursor: string | null;
  access: { everything: boolean; apps: ('qaqc' | 'lesson-learned' | 'audit')[] };
  retention: { onlineMonths: number | null };
}

export interface AuditLogFilters {
  from?: string;
  to?: string;
  app?: string;
  action?: string;
  search?: string;
}

function queryString(filters: AuditLogFilters & { before?: string; format?: string }): string {
  const params = new URLSearchParams(
    Object.entries(filters).filter(([, value]) => typeof value === 'string' && value !== '') as [string, string][],
  );
  const text = params.toString();
  return text ? `?${text}` : '';
}

export const fetchAuditLog = (filters: AuditLogFilters, before?: string) =>
  apiFetch<AuditLogPage>(`/admin/audit-log${queryString({ ...filters, before })}`);

/** Download an export. The file never touches the browser cache: the server says no-store. */
export async function downloadAuditLog(filters: AuditLogFilters, format: AuditLogExportFormat): Promise<void> {
  const blob = await apiFetchBlob(`/admin/audit-log/export${queryString({ ...filters, format })}`);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `qms360-audit-log-${new Date().toISOString().slice(0, 10)}.${format}`;
  link.click();
  URL.revokeObjectURL(url);
}
